import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { redirect, useRouter } from "@tanstack/react-router";
import { z } from "zod";

import {
  getCurrentUser,
  type UserAttributeKey,
} from "aws-amplify/auth";

import { authErrorMessage, useAuth } from "../auth";
import { LoginForm } from "@/components/login-form";
import { NewPasswordChallengeForm } from "@/components/new-password-challenge-form";

const fallback = "/" as const;

export const Route = createFileRoute("/login")({
  validateSearch: z.object({
    redirect: z.string().optional().catch(""),
  }),
  beforeLoad: async ({ search }) => {
    let isAuthenticated = false;
    try {
      isAuthenticated = !!(await getCurrentUser());
    } catch {
      /* no session */
    }
    if (isAuthenticated) {
      throw redirect({ to: search.redirect || fallback });
    }
  },
  component: LoginComponent,
});

function LoginComponent() {
  const auth = useAuth();
  const router = useRouter();
  const navigate = Route.useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<"credentials" | "new_password">(
    "credentials"
  );
  const [pendingEmail, setPendingEmail] = React.useState("");
  const [missingAttributes, setMissingAttributes] = React.useState<
    readonly UserAttributeKey[]
  >([]);

  const search = Route.useSearch();

  const finishSignIn = React.useCallback(async () => {
    await router.invalidate();
    await navigate({ to: search.redirect || fallback });
  }, [navigate, router, search.redirect]);

  const onFormSubmit = async (email: string, password: string) => {
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await auth.login(email, password);
      if (result.phase === "signed_in") {
        await finishSignIn();
        return;
      }
      setPendingEmail(email);
      setMissingAttributes(result.missingAttributes);
      setPhase("new_password");
    } catch (err) {
      setError(authErrorMessage(err) || "Login failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onNewPasswordSubmit = async (
    newPassword: string,
    confirmPassword: string,
    attributeValues: Partial<Record<UserAttributeKey, string>>
  ) => {
    setError(null);
    setIsSubmitting(true);
    try {
      if (newPassword !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (newPassword.length < 8) {
        setError("Password must be at least 8 characters long.");
        return;
      }
      for (const key of missingAttributes) {
        if (!attributeValues[key]?.trim()) {
          setError(`Please fill in all required fields.`);
          return;
        }
      }
      const attrs =
        missingAttributes.length > 0 ? attributeValues : undefined;
      await auth.completeNewPassword(newPassword, attrs);
      await finishSignIn();
    } catch (err) {
      setError(authErrorMessage(err) || "Could not update password.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onBackFromNewPassword = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await auth.logout();
      setPhase("credentials");
      setPendingEmail("");
      setMissingAttributes([]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        {phase === "credentials" ? (
          <LoginForm
            error={error}
            isSubmitting={isSubmitting}
            onSubmit={onFormSubmit}
          />
        ) : (
          <NewPasswordChallengeForm
            email={pendingEmail}
            error={error}
            isSubmitting={isSubmitting}
            missingAttributes={missingAttributes}
            onBack={onBackFromNewPassword}
            onSubmit={onNewPasswordSubmit}
          />
        )}
      </div>
    </div>
  );
}
